import moment from 'moment';
import { createSelector } from 'reselect';
import {
  prop,
  pathOr,
  flatten,
  pipe,
  map,
  groupBy,
  replace,
  values,
  path,
  find,
  propOr,
  test,
  propEq,
  sortWith,
  descend,
  ascend,
} from 'ramda';

import i18n from 'services/i18n';
import { formatDate } from 'utilities/formatters';

import { getAllResourcesEntriesList, isResources } from '../resources';

const localState = prop('patientEngagement');

export const isLoading = createSelector(
  localState,
  prop('loading'),
);

export const getStatuses = createSelector(
  localState,
  prop('statuses'),
);

export const isLoadingStatuses = createSelector(
  isLoading,
  getStatuses,
  (loading, data) => loading && !data,
);

export const getCurrentPatient = createSelector(
  localState,
  prop('currentPatient'),
);

export const isDataForPatientById = patientId =>
  createSelector(
    isResources,
    getCurrentPatient,
    (resources, patient) => resources && patient === patientId,
  );

export const getStatusesList = createSelector(
  getStatuses,
  pipe(
    pathOr([], ['entry', '0', 'resource', 'item']),
    map(prop('item')),
    flatten,
    groupBy(
      pipe(
        prop('linkId'),
        replace(/like-|read-/, ''),
      ),
    ),
    values,
    map(item => ({
      id: pipe(
        path(['0', 'linkId']),
        replace(/like-|read-/, ''),
      )(item),
      like: pipe(
        find(
          pipe(
            prop('linkId'),
            test(/like-/),
          ),
        ),
        path(['answer', '0', 'valueBoolean']),
      )(item),
      read: pipe(
        find(
          pipe(
            prop('linkId'),
            test(/read-/),
          ),
        ),
        path(['answer', '0', 'valueDateTime']),
      )(item),
    })),
  ),
);

export const getCompleteStatusesWithArticles = createSelector(
  getStatusesList,
  getAllResourcesEntriesList,
  (statuses, data) => {
    if (statuses.length === 0) {
      return [];
    }
    return pipe(
      map(resource => {
        const title = propOr('', 'description')(resource);
        const resources = propOr([], 'content')(resource);

        return map(document => {
          const documentStatus = find(propEq('id', path(['attachment', 'url'])(document)))(
            statuses,
          );
          const groupName = pipe(
            propOr([], 'extension'),
            find(propEq('url', 'subGroup')),
            prop('valueCode'),
          )(document);
          const charterName = path(['attachment', 'title'])(document);
          const charter = groupName ? `${groupName} | ${charterName}` : charterName;

          let feedBackStatus = null;
          let feedbackSort = 0;

          if (documentStatus.like !== undefined) {
            feedBackStatus = documentStatus.like
              ? i18n.t('resources.positive')
              : i18n.t('resources.nagative');
            feedbackSort = documentStatus.like ? 2 : 1;
          }

          return {
            statusSort: documentStatus.read ? moment(documentStatus.read).valueOf() : 0,
            feedbackSort,
            status: documentStatus.read
              ? i18n.t('dateTimeFormat.readStatusFormat', {
                  date: formatDate(i18n.t('dateTimeFormat.engagementDate'))(documentStatus.read),
                  time: formatDate(i18n.t('dateTimeFormat.engagementTime'))(documentStatus.read),
                })
              : i18n.t('resources.unopened'),
            module: title === 'FAQ' ? 'FAQ' : 'PER',
            charter,
            feedback: feedBackStatus,
          };
        })(resources);
      }),
      flatten,
    )(data);
  },
);

export const getSortOrder = createSelector(
  localState,
  prop('order'),
);

export const getSortBy = createSelector(
  localState,
  prop('sortBy'),
);

export const getSortedPatientEngagementList = createSelector(
  getCompleteStatusesWithArticles,
  getSortOrder,
  getSortBy,
  (list, order, sortByField) =>
    sortWith([order === 'asc' ? ascend(prop(sortByField)) : descend(prop(sortByField))])(list),
);
